import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, BarChart3, Users, Briefcase, Clock, Calendar, 
  TrendingUp, FileText, Download, Printer, RefreshCw,
  AlertTriangle, CheckCircle, XCircle, User
} from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [workSummary, setWorkSummary] = useState(null);
  const [employeePerformance, setEmployeePerformance] = useState(null);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [monthlyAttendance, setMonthlyAttendance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dateRange, setDateRange] = useState({
    start_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchReports();
    }
  }, [dateRange, user]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [summaryRes, performanceRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/reports/work-summary?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/reports/employee-performance?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/attendance/report?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setWorkSummary(summaryRes.data);
      setEmployeePerformance(performanceRes.data);
      setAttendanceReport(attendanceRes.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
    setLoading(false);
  };

  const fetchMonthlyAttendance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [year, month] = selectedMonth.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = new Date(year, month, 0).toISOString().split('T')[0];
      
      const response = await axios.get(
        `${API}/attendance/report?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyAttendance(response.data);
    } catch (err) {
      console.error('Error fetching monthly attendance:', err);
    }
    setLoading(false);
  };

  const printReport = () => {
    window.print();
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('ar-SA', { month: 'long', year: 'numeric' });
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-body">هذه الصفحة متاحة للمدير فقط</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-2 bg-gold text-white rounded-md font-body"
          >
            العودة للوحة التحكم
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-xl font-heading font-bold text-white">التقارير</h1>
          </div>
          <button
            onClick={printReport}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30"
          >
            <Printer className="w-4 h-4" />
            طباعة
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* التبويبات */}
        <div className="flex flex-wrap gap-2 mb-6 print:hidden">
          {['summary', 'attendance', 'monthly'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-body transition-all ${
                activeTab === tab
                  ? 'bg-gold text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab === 'summary' && 'ملخص العمل'}
              {tab === 'attendance' && 'تقرير الحضور'}
              {tab === 'monthly' && 'تقرير الحضور الشهري'}
            </button>
          ))}
        </div>

        {/* فلتر التاريخ */}
        {activeTab !== 'monthly' && (
          <div className="bg-white rounded-xl p-4 mb-6 shadow-sm print:hidden">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1 font-body">من تاريخ</label>
                <input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                  className="px-3 py-2 border border-gray-200 rounded-lg font-body"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1 font-body">إلى تاريخ</label>
                <input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                  className="px-3 py-2 border border-gray-200 rounded-lg font-body"
                />
              </div>
              <button
                onClick={fetchReports}
                disabled={loading}
                className="mt-6 flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                تحديث
              </button>
            </div>
          </div>
        )}

        {/* تقرير الحضور الشهري */}
        {activeTab === 'monthly' && (
          <div className="space-y-6">
            {/* اختيار الشهر */}
            <div className="bg-white rounded-xl p-4 shadow-sm print:hidden">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1 font-body">اختر الشهر</label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg font-body"
                  />
                </div>
                <button
                  onClick={fetchMonthlyAttendance}
                  disabled={loading}
                  className="mt-6 flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark disabled:opacity-50"
                >
                  <FileText className="w-4 h-4" />
                  إنشاء التقرير
                </button>
              </div>
            </div>

            {/* عرض التقرير الشهري */}
            {monthlyAttendance && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" id="monthly-report">
                {/* رأس التقرير */}
                <div className="p-6 border-b border-gray-100 bg-gradient-to-l from-primary to-primary-800 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-heading font-bold">تقرير الحضور الشهري</h2>
                      <p className="text-white/80 font-body">{getMonthName(selectedMonth)}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-sm text-white/70">مكتب المحامي</p>
                      <p className="font-heading font-bold">هشام يوسف الخياط</p>
                    </div>
                  </div>
                </div>

                {/* ملخص التقرير */}
                <div className="p-6 border-b border-gray-100">
                  <h3 className="text-lg font-heading font-semibold text-primary mb-4">ملخص الشهر</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">{monthlyAttendance.total_records}</p>
                      <p className="text-sm text-blue-700 font-body">إجمالي السجلات</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{monthlyAttendance.present_days}</p>
                      <p className="text-sm text-green-700 font-body">أيام الحضور</p>
                    </div>
                    <div className="bg-gold/10 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-gold">{monthlyAttendance.total_hours}</p>
                      <p className="text-sm text-gold-dark font-body">إجمالي الساعات</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{monthlyAttendance.by_employee?.length || 0}</p>
                      <p className="text-sm text-purple-700 font-body">عدد الموظفين</p>
                    </div>
                  </div>
                </div>

                {/* تفاصيل كل موظف */}
                <div className="p-6">
                  <h3 className="text-lg font-heading font-semibold text-primary mb-4">تفاصيل الموظفين</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">الموظف</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">أيام الحضور</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">إجمالي الساعات</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">متوسط الساعات/يوم</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">الحالة</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {monthlyAttendance.by_employee?.map((emp, idx) => {
                          const avgHours = emp.days > 0 ? (emp.total_hours / emp.days).toFixed(1) : 0;
                          const isLow = avgHours < 6;
                          const isOvertime = avgHours > 9;
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm">
                                    {emp.user_name?.charAt(0)}
                                  </div>
                                  <span className="font-body">{emp.user_name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center font-semibold">{emp.days}</td>
                              <td className="px-4 py-3 text-center font-semibold text-gold">{emp.total_hours?.toFixed(1)} س</td>
                              <td className="px-4 py-3 text-center">{avgHours} س</td>
                              <td className="px-4 py-3 text-center">
                                {isLow ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs">
                                    <AlertTriangle className="w-3 h-3" />
                                    ساعات منخفضة
                                  </span>
                                ) : isOvertime ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-600 rounded-full text-xs">
                                    <Clock className="w-3 h-3" />
                                    ساعات إضافية
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs">
                                    <CheckCircle className="w-3 h-3" />
                                    طبيعي
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* تذييل التقرير */}
                <div className="p-4 bg-gray-50 text-center text-sm text-gray-500 font-body">
                  تم إنشاء التقرير في: {new Date().toLocaleString('ar-SA')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ملخص العمل */}
        {activeTab === 'summary' && workSummary && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Briefcase className="w-8 h-8 text-blue-500" />
                  <h3 className="font-heading font-semibold text-primary">المهام</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-body">الإجمالي</span>
                    <span className="font-bold">{workSummary.total_assignments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-body">المكتملة</span>
                    <span className="font-bold text-green-600">{workSummary.completed_assignments}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-body">المعلقة</span>
                    <span className="font-bold text-yellow-600">{workSummary.pending_assignments}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-8 h-8 text-gold" />
                  <h3 className="font-heading font-semibold text-primary">القضايا</h3>
                </div>
                <p className="text-3xl font-bold text-primary">{workSummary.total_cases}</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-green-500" />
                  <h3 className="font-heading font-semibold text-primary">الاستشارات</h3>
                </div>
                <p className="text-3xl font-bold text-primary">{workSummary.total_consultations}</p>
              </div>
            </div>
          </div>
        )}

        {/* تقرير الحضور */}
        {activeTab === 'attendance' && attendanceReport && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-heading font-semibold text-primary">تقرير الحضور للفترة</h3>
              <p className="text-sm text-gray-500 font-body">
                من {dateRange.start_date} إلى {dateRange.end_date}
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{attendanceReport.total_records}</p>
                  <p className="text-sm text-blue-700 font-body">إجمالي السجلات</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{attendanceReport.present_days}</p>
                  <p className="text-sm text-green-700 font-body">أيام الحضور</p>
                </div>
                <div className="bg-gold/10 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gold">{attendanceReport.total_hours}</p>
                  <p className="text-sm text-gold-dark font-body">إجمالي الساعات</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">الموظف</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">عدد الأيام</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600">إجمالي الساعات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attendanceReport.by_employee?.map((emp, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-body">{emp.user_name}</td>
                        <td className="px-4 py-3 text-center">{emp.days}</td>
                        <td className="px-4 py-3 text-center font-semibold text-gold">{emp.total_hours?.toFixed(1)} س</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-gold animate-spin" />
          </div>
        )}
      </main>
    </div>
  );
}
